<template>
  <div class="py-[80px] lg:py-[120px]">
    <div class="container">
      <BaseBreadcrumb class="max-w-[998px]" v-bind="{ menu }" />
    </div>
    <BlogSingle :single="blogSingleData" :loading="loading" />
    <StartTrading class="mt-10" />
  </div>
</template>

<script setup lang="ts">
import BaseBreadcrumb from "@/modules/Landing/components/Base/Breadcrumb/Breadcrumb.vue";
import BlogSingle from "@/modules/Landing/components/Blog/Single.vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import ApiService from "@/services/ApiService";
import { useFingerprint } from "@/composables/useFingerPrint";
import { ref, onMounted, computed, watchEffect } from "vue";
import { useHead } from "@vueuse/head";
import StartTrading from "@/modules/Landing/components/Section/StartTrading.vue";

const { t } = useI18n();

const route = useRoute();
const loading = ref(true);
const blogSingleData = ref<any>({});

const menu = computed(() => {
  return [
    {
      title: t("blogs"),
      link: "/blogs",
    },
    {
      title: blogSingleData.value?.title ?? t("News Single"),
      link: "", // Empty string to comply with type
    },
  ];
});

// Function to strip HTML tags from content
const stripHtmlTags = (htmlContent = ""): string => {
  return htmlContent?.replace(/<[^>]*>?/gm, "") || "";
};

// Generate clean description from content
const getDescription = computed(() => {
  const cleanContent = stripHtmlTags(blogSingleData.value?.content);
  // Limit description to reasonable length
  return cleanContent?.length > 160
    ? cleanContent.substring(0, 157) + "..."
    : cleanContent || "";
});

// SEO-optimized keywords from blog content
const getKeywords = computed(() => {
  const content = stripHtmlTags(blogSingleData.value?.content || "");
  // Extract potential keywords (simplified approach)
  const words = content
    .split(" ")
    .filter((word) => word.length > 4)
    .slice(0, 10)
    .join(", ");
  return words || "trading, automated, investment, robot";
});

// Watch for blog data changes and update head meta tags
watchEffect(() => {
  const title =
    blogSingleData.value?.title ||
    "Saraf Robot - The Future of Trading is Automated";
  const description =
    getDescription.value ||
    "Saraf Robot is an advanced trading bot that handles your investment strategy, giving you the best experience with automated solutions.";
  const coverImage = blogSingleData.value?.cover || "/og-image.webp";
  const keywords = getKeywords.value;
  const publishDate = blogSingleData.value?.created_at || "";

  useHead({
    title,
    meta: [
      { name: "description", content: description },
      { name: "keywords", content: keywords },
      // Open Graph
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:image", content: coverImage },
      { property: "og:type", content: "article" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:width", content: "1200" },
      {
        property: "og:url",
        content: `https://app.sarafrobot.com${route.path}`,
      },
      // Twitter
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@sarafrobot" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
      { name: "twitter:image", content: coverImage },
      {
        name: "twitter:url",
        content: `https://app.sarafrobot.com${route.path}`,
      },
      { name: "twitter:image:height", content: "630" },
      { name: "twitter:image:width", content: "1200" },
      // Article specific meta tags
      ...(publishDate
        ? [{ property: "article:published_time", content: publishDate }]
        : []),
      ...(blogSingleData.value?.category
        ? [
            {
              property: "article:section",
              content: blogSingleData.value.category,
            },
          ]
        : []),
    ],
    link: [
      { rel: "canonical", href: `https://app.sarafrobot.com${route.path}` },
    ],
  });
});

const fetchBlogSingle = () => {
  ApiService.vueInstance.axios.defaults.headers.common["fingerprint"] =
    useFingerprint().getFingerprint();
  ApiService.get(`landing/blogs/${route.params.slug}/`)
    .then((res) => {
      blogSingleData.value = res?.data;
    })
    .finally(() => {
      loading.value = false;
    });
};

onMounted(() => {
  fetchBlogSingle();
});
</script>
